import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Tabs, Tab, Spinner } from '@nextui-org/react';
import {
  Heart,
  Trophy,
  Users,
  Beer,
  ShoppingBag,
  Layers,
  MapPin,
  ChevronRight,
  Calendar as CalendarIcon,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import tournamentService, { Tournament } from '../services/tournamentService';
import appDataService, { Club, Pub, Shop, Series } from '../services/appDataService';
import userService, { FavouriteType } from '../services/userService';

const TEAL = '#3FCED2';

type TabKey = 'tournaments' | 'series' | 'pubs' | 'clubs' | 'shops';

function getId(e: { _id?: string | null; id?: string | null }): string | null {
  return (e?._id || e?.id) ?? null;
}

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  const initialTab = (searchParams.get('tab') as TabKey) || 'tournaments';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const [loading, setLoading] = useState(true);
  const [favTournaments, setFavTournaments] = useState<Tournament[]>([]);
  const [favSeries, setFavSeries] = useState<Series[]>([]);
  const [favPubs, setFavPubs] = useState<Pub[]>([]);
  const [favClubs, setFavClubs] = useState<Club[]>([]);
  const [favShops, setFavShops] = useState<Shop[]>([]);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Echte Favoriten-Daten über die /api/user/saved/* Endpunkte laden
      const [tournamentsData, seriesData, clubsData, pubsData, shopsData] = await Promise.all([
        userService.getSavedTournaments(),
        userService.getSavedSeries(),
        userService.getSavedClubs(),
        userService.getSavedPubs(),
        userService.getSavedShops(),
      ]);
      setFavTournaments(tournamentsData as Tournament[]);
      setFavSeries(seriesData as Series[]);
      setFavClubs(clubsData as Club[]);
      setFavPubs(pubsData as Pub[]);
      setFavShops(shopsData as Shop[]);
    } catch (err) {
      console.error('Fehler beim Laden der Favoriten:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    window.scrollTo({ top: 0 });
  }, [loadAll]);

  // Entfernen mit optimistic UI: erst lokal ausblenden, bei Fehler wiederherstellen
  const removingIds = useRef<Set<string>>(new Set());

  const handleRemove = useCallback(
    async (id: string, type: FavouriteType) => {
      if (!id || removingIds.current.has(id)) return;
      removingIds.current.add(id);
      setRemoveError(null);

      const setterByType: Record<FavouriteType, React.Dispatch<React.SetStateAction<any[]>>> = {
        TOURNAMENT: setFavTournaments as React.Dispatch<React.SetStateAction<any[]>>,
        SERIES: setFavSeries as React.Dispatch<React.SetStateAction<any[]>>,
        CLUB: setFavClubs as React.Dispatch<React.SetStateAction<any[]>>,
        PUB: setFavPubs as React.Dispatch<React.SetStateAction<any[]>>,
        SHOP: setFavShops as React.Dispatch<React.SetStateAction<any[]>>,
      };
      const setter = setterByType[type];

      let removedItem: any = null;
      setter((prev) => {
        removedItem = prev.find((it) => getId(it) === id) ?? null;
        return prev.filter((it) => getId(it) !== id);
      });

      try {
        await userService.removeFavourite(id, type);
        // savedX-Arrays im AuthContext aktualisieren (z. B. für Zähler im Profil)
        refreshUser().catch(() => null);
      } catch (err) {
        console.error('Favorit konnte nicht entfernt werden:', err);
        // Optimistic UI zurückrollen
        if (removedItem) {
          setter((prev) => [...prev, removedItem]);
        }
        setRemoveError('Favorit konnte nicht entfernt werden. Bitte versuche es erneut.');
      } finally {
        removingIds.current.delete(id);
      }
    },
    [refreshUser]
  );

  const handleTabChange = (key: React.Key) => {
    const k = String(key) as TabKey;
    setActiveTab(k);
    setSearchParams({ tab: k }, { replace: true });
  };

  const counts = {
    tournaments: favTournaments.length,
    series: favSeries.length,
    pubs: favPubs.length,
    clubs: favClubs.length,
    shops: favShops.length,
  };
  const totalCount = counts.tournaments + counts.series + counts.pubs + counts.clubs + counts.shops;

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Favoriten - DartCloud</title>
        <meta name="description" content="Deine gespeicherten Turniere, Vereine, Spielstätten, Shops und Turnierserien" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${TEAL}22` }}
          >
            <Heart className="w-5 h-5" style={{ color: TEAL }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Favoriten</h1>
            <p className="text-sm text-zinc-400">
              {totalCount} gespeicherte {totalCount === 1 ? 'Eintrag' : 'Einträge'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={handleTabChange}
            variant="solid"
            classNames={{
              tabList: 'bg-zinc-900/70 border border-zinc-800 p-1 rounded-xl flex-wrap',
              cursor: 'bg-zinc-800',
              tab: 'text-zinc-300 data-[selected=true]:text-white',
              tabContent: 'group-data-[selected=true]:text-white',
            }}
          >
            <Tab
              key="tournaments"
              title={
                <TabLabel icon={<Trophy className="w-4 h-4" />} label="Turniere" count={counts.tournaments} />
              }
            />
            <Tab
              key="series"
              title={
                <TabLabel icon={<Layers className="w-4 h-4" />} label="Serien" count={counts.series} />
              }
            />
            <Tab
              key="pubs"
              title={
                <TabLabel icon={<Beer className="w-4 h-4" />} label="Spielstätten" count={counts.pubs} />
              }
            />
            <Tab
              key="clubs"
              title={
                <TabLabel icon={<Users className="w-4 h-4" />} label="Vereine" count={counts.clubs} />
              }
            />
            <Tab
              key="shops"
              title={
                <TabLabel icon={<ShoppingBag className="w-4 h-4" />} label="Shops" count={counts.shops} />
              }
            />
          </Tabs>
        </div>

        {/* Fehler beim Entfernen */}
        {removeError && (
          <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {removeError}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner color="primary" size="lg" />
          </div>
        ) : (
          <>
            {activeTab === 'tournaments' && (
              <FavoritesList
                emptyIcon={<Trophy className="w-12 h-12" />}
                emptyTitle="Keine gespeicherten Turniere"
                emptyText="Speichere Turniere, um sie hier wiederzufinden."
                items={favTournaments.map((t) => ({
                  key: getId(t) || t.slug || '',
                  title: t.name || 'Turnier',
                  subtitle: tournamentService.formatAddress(t.address),
                  meta: formatNextDate(tournamentService.getNextTimestamp(t)),
                  image: null,
                  onClick: () => navigate(`/turnier/${t.slug || getId(t)}`),
                  onRemove: () => {
                    const id = getId(t);
                    if (id) handleRemove(id, 'TOURNAMENT');
                  },
                }))}
              />
            )}

            {activeTab === 'series' && (
              <FavoritesList
                emptyIcon={<Layers className="w-12 h-12" />}
                emptyTitle="Keine gespeicherten Turnierserien"
                emptyText="Speichere Turnierserien, um sie hier wiederzufinden."
                items={favSeries.map((s) => ({
                  key: getId(s) || s.slug || '',
                  title: s.name || 'Serie',
                  subtitle: appDataService.formatAddress(s.address),
                  meta: null,
                  image: null,
                  onClick: () => navigate(`/turnierserie/${s.slug || getId(s)}`),
                  onRemove: () => {
                    const id = getId(s);
                    if (id) handleRemove(id, 'SERIES');
                  },
                }))}
              />
            )}

            {activeTab === 'pubs' && (
              <FavoritesList
                emptyIcon={<Beer className="w-12 h-12" />}
                emptyTitle="Keine gespeicherten Spielstätten"
                emptyText="Speichere Spielstätten, um sie hier wiederzufinden."
                items={favPubs.map((p) => ({
                  key: getId(p) || p.slug || '',
                  title: appDataService.formatName(p) || 'Spielstätte',
                  subtitle: appDataService.formatAddress(p.address),
                  meta: null,
                  image: null,
                  onClick: () => navigate(`/spielstaette/${p.slug || getId(p)}`),
                  onRemove: () => {
                    const id = getId(p);
                    if (id) handleRemove(id, 'PUB');
                  },
                }))}
              />
            )}

            {activeTab === 'clubs' && (
              <FavoritesList
                emptyIcon={<Users className="w-12 h-12" />}
                emptyTitle="Keine gespeicherten Vereine"
                emptyText="Speichere Vereine, um sie hier wiederzufinden."
                items={favClubs.map((c) => ({
                  key: getId(c) || c.slug || '',
                  title: appDataService.formatName(c) || 'Verein',
                  subtitle: appDataService.formatAddress(c.address),
                  meta: null,
                  image: null,
                  onClick: () => navigate(`/verein/${c.slug || getId(c)}`),
                  onRemove: () => {
                    const id = getId(c);
                    if (id) handleRemove(id, 'CLUB');
                  },
                }))}
              />
            )}

            {activeTab === 'shops' && (
              <FavoritesList
                emptyIcon={<ShoppingBag className="w-12 h-12" />}
                emptyTitle="Keine gespeicherten Shops"
                emptyText="Speichere Shops, um sie hier wiederzufinden."
                items={favShops.map((s) => ({
                  key: getId(s) || s.slug || '',
                  title: appDataService.formatName(s) || 'Shop',
                  subtitle: appDataService.formatAddress(s.address),
                  meta: null,
                  image: null,
                  onClick: () => navigate(`/shop/${s.slug || getId(s)}`),
                  onRemove: () => {
                    const id = getId(s);
                    if (id) handleRemove(id, 'SHOP');
                  },
                }))}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TabLabel: React.FC<{ icon: React.ReactNode; label: string; count: number }> = ({ icon, label, count }) => (
  <div className="flex items-center gap-2 px-1 py-0.5">
    {icon}
    <span className="hidden sm:inline">{label}</span>
    <span
      className="text-xs px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
    >
      {count}
    </span>
  </div>
);

interface FavListItem {
  key: string;
  title: string;
  subtitle: string;
  meta: string | null;
  image: string | null;
  onClick: () => void;
  onRemove: () => void;
}

const FavoritesList: React.FC<{
  items: FavListItem[];
  emptyIcon: React.ReactNode;
  emptyTitle: string;
  emptyText: string;
}> = ({ items, emptyIcon, emptyTitle, emptyText }) => {
  if (items.length === 0) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl py-16 px-6 text-center">
        <div className="inline-flex w-16 h-16 rounded-full bg-zinc-800/60 items-center justify-center text-zinc-500 mb-4">
          {emptyIcon}
        </div>
        <h3 className="text-lg font-semibold mb-1">{emptyTitle}</h3>
        <p className="text-sm text-zinc-400">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((item) => (
        <div
          key={item.key}
          role="button"
          tabIndex={0}
          onClick={item.onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              item.onClick();
            }
          }}
          className="cursor-pointer text-left bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl p-4 transition-colors flex items-center gap-3 group"
        >
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate group-hover:text-white">{item.title}</div>
            {item.subtitle && (
              <div className="text-xs text-zinc-400 truncate flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {item.subtitle}
              </div>
            )}
            {item.meta && (
              <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                {item.meta}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              item.onRemove();
            }}
            className="p-2 rounded-full text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
            title="Aus Favoriten entfernen"
            aria-label="Aus Favoriten entfernen"
          >
            <X className="w-4 h-4" />
          </button>
          <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0 group-hover:text-zinc-300" />
        </div>
      ))}
    </div>
  );
};

function formatNextDate(ts?: number | null): string | null {
  if (!ts || typeof ts !== 'number') return null;
  try {
    return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
}

export default FavoritesPage;
